import { MigrationPreviewItem, MigrationPreviewRow } from "@swivl-mono/api-helper";
import { Checkbox } from "@swivl-mono/ui";
import { Link } from "react-router-dom";

interface MigrationItemRowProps {
  item: MigrationPreviewItem;
  selected: boolean;
  expanded: boolean;
  onToggleSelected: () => void;
  onToggleExpanded: () => void;
}

export function MigrationItemRow({ item, selected, expanded, onToggleSelected, onToggleExpanded }: MigrationItemRowProps): React.ReactElement {
  const { summary } = item;
  const totalRows = item.rows.length;
  // A quarantined row with an open ticket is "tracked" — no longer an active flag.
  const activeQuarantine = item.rows.filter((r) => r.quarantined && !r.ticket).length;
  const ticketed = item.rows.filter((r) => r.quarantined && r.ticket).length;

  return (
    <div className={`px-4 py-3 ${selected ? "" : "opacity-50"}`}>
      <div className="flex items-start gap-3">
        <div className="mt-1">
          <Checkbox checked={selected} onCheckedChange={onToggleSelected} name={`select-${item.specId}`} />
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="body-sm-alt text-grey-800">{item.label}</span>
            <code className="body-mono rounded bg-grey-100 px-1.5 py-0.5 text-grey-600">
              {item.brandColorsTarget ? "brand.colors ← theme.primaryBackground / theme.secondaryBackground" : `${item.toKey} ← ${item.fromKey}`}
            </code>
          </div>
          <div className="body-xs mt-1 font-normal text-grey-500">
            {summary.wouldWriteCount > 0 && <span className="mr-3 text-teal-600">would write {summary.wouldWriteCount}</span>}
            {summary.alreadyMatchCount > 0 && <span className="mr-3 text-grey-400">{summary.alreadyMatchCount} already match</span>}
            {activeQuarantine > 0 && <span className="mr-3 text-red-600">{activeQuarantine} flagged for review</span>}
            {ticketed > 0 && <span className="mr-3 text-grey-500">{ticketed} tracked by ticket</span>}
            {summary.warningCount > 0 && <span className="mr-3 text-yellow-700">{summary.warningCount} to verify</span>}
            {summary.hoistedCount > 0 && <span className="mr-3 text-teal-700">{summary.hoistedCount} written once at org/group</span>}
            {summary.inheritsCount > 0 && <span className="mr-3 text-grey-400">{summary.inheritsCount} inherit instead</span>}
            {summary.removeCount > 0 && <span className="mr-3 text-orange-700">{summary.removeCount} removed to inherit</span>}
            {totalRows === 0 && <span className="text-grey-400">no legacy values found</span>}
          </div>
        </div>

        {totalRows > 0 && (
          <button type="button" className="ml-auto text-xs text-orange-600 hover:underline" onClick={onToggleExpanded}>
            {expanded ? "Hide details" : `Show details (${totalRows})`}
          </button>
        )}
      </div>

      {expanded && totalRows > 0 && (
        <div className="mt-3 overflow-x-auto rounded border border-grey-100">
          <table className="w-full text-xs">
            <thead className="bg-grey-50 text-left text-grey-500">
              <tr>
                <th className="px-3 py-2 font-medium">Scope</th>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Legacy now</th>
                <th className="px-3 py-2 font-medium">Cortex now</th>
                <th className="px-3 py-2 font-medium">Will write</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-grey-100">
              {item.rows.map((row) => (
                <DetailRow key={`${row.scope}:${row.scopeId}`} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DetailRow({ row }: { row: MigrationPreviewRow }): React.ReactElement {
  const willWriteCell = row.quarantined ? (
    row.ticket ? (
      // Tracked by an open ticket → neutral, no longer an active red flag.
      <span className="text-grey-500">
        tracked ·{" "}
        <Link to={`/inbox/ticket/${row.ticket.number}`} className="text-orange-600 hover:underline">
          ticket #{row.ticket.number}
        </Link>{" "}
        open since {new Date(row.ticket.createdAt).toLocaleDateString()}
      </span>
    ) : (
      <span className="text-red-600">⚠ {quarantineLabel(row.quarantineReason)}</span>
    )
  ) : row.warning ? (
    // Warning wins over "matches" so the caution persists after apply (once written, it matches).
    <span className="text-yellow-700">
      {row.willWriteDisplay ?? "—"} — ⚠ {warningLabel(row.warning)}
    </span>
  ) : row.collapse === "remove" ? (
    // An explicit row that now equals what its scope inherits — deleted on apply so the scope stops looking customized.
    // No `inheritsFrom` means an org/group row holding one location's own value (a per-location field): removed, nothing inherited.
    <span className="text-orange-700">
      {row.inheritsFrom
        ? `— will remove, inherits from ${row.inheritsFrom}`
        : "— will remove: holds one location's value; each location keeps its own"}
    </span>
  ) : row.collapse === "inherits" ? (
    // Not written: the scope above now resolves to this same value, so a row here would only make the location look customized.
    <span className="text-grey-400">— inherits from {row.inheritsFrom ?? "org"}, not written</span>
  ) : row.alreadyMatches ? (
    <span className="text-grey-400">
      — matches{row.collapse === "hoisted" && row.covers != null ? ` · covers ${row.covers} location${row.covers === 1 ? "" : "s"}` : ""}
    </span>
  ) : (
    <span>
      {row.willWriteDisplay ?? "—"}
      {row.collapse === "hoisted" && row.covers != null && (
        <span className="text-teal-700">
          {" "}
          · written once here, covers {row.covers} location{row.covers === 1 ? "" : "s"}
        </span>
      )}
    </span>
  );

  const rowClass = row.quarantined
    ? row.ticket
      ? "bg-grey-50/50 text-grey-500"
      : "bg-red-50 text-red-700"
    : row.warning
      ? "bg-yellow-50 text-yellow-700"
      : row.alreadyMatches || row.collapse === "inherits"
        ? "bg-grey-50/50 text-grey-400"
        : "";

  return (
    <tr className={rowClass}>
      <td className="px-3 py-2 font-medium uppercase">{row.scope}</td>
      <td className="px-3 py-2">{row.scopeLabel}</td>
      <td className="px-3 py-2">{row.fromValueDisplay ?? "—"}</td>
      <td className="px-3 py-2">{row.currentToValueDisplay ?? "—"}</td>
      <td className="px-3 py-2">{willWriteCell}</td>
    </tr>
  );
}

/** Plain-English reason a row was skipped, for the red "won't migrate" cell. */
export function quarantineLabel(reason: string | null): string {
  switch (reason) {
    case "non-boolean-value":
      return "won't migrate — not a yes/no value";
    case "conflicting-sources":
      return "won't migrate — conflicting legacy values";
    case "unparseable-hours":
      return "won't migrate — couldn't read hours";
    case "uncertain-hours":
      return "won't migrate — hours only partly readable, check them";
    case "unclear-keypad":
      return "won't migrate — keypad keys described only in prose, write the instruction";
    case "invalid-timezone":
      return "won't migrate — not a valid timezone";
    case "invalid-phone-number":
      return "won't migrate — not a single dialable phone number";
    case "unmapped-variable":
      return "won't migrate — uses a legacy variable with no Snowy equivalent";
    default:
      return "won't migrate — needs review";
  }
}

/** Plain-English caution for a row that WILL migrate but was a guess. */
export function warningLabel(warning: string | null): string {
  switch (warning) {
    case "assumed-all-days":
      return "assumed all days — verify";
    default:
      return "verify";
  }
}
